import React, { useRef, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import useUpload from "../../../hooks/useUpload";
import { ApiConfig, API_ORDER_ROUTER, API_UPLOAD_ROUTER } from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import ImageCustom from "../../../components/custom/image-custom";

const MAX_PHOTOS = 12;
const ACCEPT = "image/jpeg,image/jpg,image/png";

interface Props {
  orderId: number;
  orderCode?: string;
  /** Key bước trong caption_images (2 lấy hàng · 9 kiểm đếm nhận · 18 kiểm đếm giao · 5 giao hàng). */
  captionKey: number;
  stepLabel: string;
  initialUrls: string[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Thay bộ ảnh xác nhận của 1 bước (`POST :id/step-photos`, quyền ORDER_PHOTO_EDIT). Xoá / thêm ảnh rồi lưu cả bộ;
 * cần ≥ 1 ảnh, tối đa 12. BE ghi nhật ký ảnh cũ / mới, ảnh gốc trong lịch sử bước giữ nguyên.
 */
const StepPhotosEditModal: React.FC<Props> = ({ orderId, orderCode, captionKey, stepLabel, initialUrls, onClose, onSaved }) => {
  const { request: post } = useFetchData.Post<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { uploadFiles, isUploading, uploadProgress } = useUpload<{ type: string; path: string }[]>(API_UPLOAD_ROUTER.API_UPLOAD);
  const [urls, setUrls] = useState<string[]>(initialUrls);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const remaining = MAX_PHOTOS - urls.length;
  const changed = urls.length !== initialUrls.length || urls.some((u, i) => u !== initialUrls[i]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (files.length > remaining) {
      showToast({ message: `Chỉ thêm được ${remaining} ảnh nữa (tối đa ${MAX_PHOTOS} ảnh)`, type: "warning" });
      e.target.value = "";
      return;
    }
    const res = await uploadFiles(files);
    e.target.value = "";
    const paths = ((res as any)?.data || []).map((f: any) => f?.path).filter(Boolean) as string[];
    if (!res || !isSuccess(res) || paths.length === 0) {
      showToast({ message: res?.message || "Tải ảnh lên thất bại", type: "error" });
      return;
    }
    setUrls((prev) => [...prev, ...paths].slice(0, MAX_PHOTOS));
  };

  const save = async () => {
    if (urls.length === 0 || !changed) return;
    setSaving(true);
    try {
      const res = await post({
        endPoint: API_ORDER_ROUTER.API_STEP_PHOTOS(orderId),
        data: { caption_key: captionKey, images: urls },
      });
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message || "Lưu ảnh thất bại", type: "error" });
        return;
      }
      showToast({ message: `Đã cập nhật ${stepLabel.toLowerCase()}`, type: "success" });
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || "Lưu ảnh thất bại", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || isUploading;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[640px] max-w-[95vw] max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Sửa {stepLabel.toLowerCase()}</h3>
          <p className="text-xs text-slate-500 mt-1">
            Đơn {orderCode ? <b>{orderCode}</b> : `#${orderId}`} — {urls.length}/{MAX_PHOTOS} ảnh
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {urls.map((url, i) => (
              <div key={`${url}-${i}`} className="relative group">
                <ImageCustom
                  url={url}
                  isLocal={false}
                  isUser={false}
                  isReview={true}
                  className="w-full h-28 object-cover rounded-lg border border-slate-200"
                />
                <button
                  type="button"
                  title="Xoá ảnh"
                  disabled={busy}
                  onClick={() => setUrls((prev) => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-sm leading-none hover:bg-red-600 transition"
                >
                  ×
                </button>
              </div>
            ))}
            {remaining > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="h-28 rounded-lg border-2 border-dashed border-purple-300 text-purple-600 text-sm hover:bg-purple-50 transition disabled:opacity-50"
              >
                {isUploading ? `Đang tải ${uploadProgress}%` : "+ Thêm ảnh"}
              </button>
            )}
          </div>
          <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={onPick} />
          {urls.length === 0 && (
            <p className="text-xs text-red-500 mt-3">Cần giữ ít nhất 1 ảnh.</p>
          )}
          <p className="text-xs text-slate-500 mt-3">
            Ảnh gốc lúc làm bước vẫn được lưu trong lịch sử xử lý đơn; mọi lần sửa đều ghi nhật ký.
          </p>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
          >
            Trở lại
          </button>
          <button
            onClick={save}
            disabled={busy || urls.length === 0 || !changed}
            className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : "Lưu ảnh"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepPhotosEditModal;
